import {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
import CustomModal from "../CustomModal";
import Row from "../../../components/Row";
import Column from "../../../components/Column";
import Text from "../../../components/Text";
import styled from "styled-components";
import { styles } from "../../../styles/themes/style";
import DropdownMenu from "../../../components/dropdown/DropdownMenu";
import DropdownItem from "../../../components/dropdown/DropdownItem";
import Checkbox from "../../../components/Checkbox";
import { IKanban } from "../../../interfaces/models/kanban";
import { useConnectionsHandlers } from "../../../services/actions/connections/useConnectionsHandlers";
import { IConnection } from "../../../interfaces/models/connection";
import Button from "../../Button";
import { useAppHandlers } from "../../../services/actions/app/useAppHandlers";
import { useAuthStore } from "../../../stores/auth/useAuthStore";

interface IAddToKanbanModalProps {
  selectedConnections?: IConnection[];
  onClose?: () => void;
}

const AddToKanbanModal: FunctionComponent<IAddToKanbanModalProps> = ({
  selectedConnections = [],
  onClose,
}) => {
  const { bulkAddConnectionsToKanbanHandler, getKanbansHandler } = useConnectionsHandlers();
  const { hideModal, showErrorToast, showSuccessToast } = useAppHandlers();
  const { store: { workspace } } = useAuthStore();

  const [kanbans, setKanbans] = useState<IKanban[]>([]);
  const [selectedKanbanIds, setSelectedKanbanIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch kanbans on mount
  useEffect(() => {
    const fetchKanbans = async () => {
      if (workspace?._id) {
        setLoading(true);
        const result = await getKanbansHandler(workspace._id);
        if (result?.success && result?.data) {
          setKanbans(result.data);
        }
        setLoading(false);
      }
    };
    fetchKanbans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?._id]);

  const handleKanbanSelect = useCallback((kanbanId: string) => {
    setSelectedKanbanIds((prev) => {
      if (prev.includes(kanbanId)) {
        // Remove from selection
        return prev.filter((id) => id !== kanbanId);
      } else {
        // Add to selection
        return [...prev, kanbanId];
      }
    });
  }, []);

  const handleSubmit = async () => {
    if (selectedKanbanIds.length === 0) {
      showErrorToast({ message: "Please select at least one pipeline" });
      return;
    }

    setSubmitting(true);
    try {
      const result = await bulkAddConnectionsToKanbanHandler(
        selectedConnections,
        selectedKanbanIds
      );
      
      if (result?.success) {
        const count = selectedConnections.length;
        showSuccessToast({
          message: `${count} contact${count !== 1 ? 's' : ''} successfully added to ${selectedKanbanIds.length} pipeline${selectedKanbanIds.length !== 1 ? 's' : ''}.`,
        });
      }
      // Close modal after API call completes (success or not)
      hideModal();
      if (onClose) onClose();
    } catch (error) {
      showErrorToast({ message: "Failed to add contacts to pipeline" });
      // Close modal even on error
      hideModal();
      if (onClose) onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const selectedKanbanName =
    selectedKanbanIds.length === 0
      ? "Choose pipeline"
      : selectedKanbanIds.length === 1
      ? kanbans.find((k) => k._id === selectedKanbanIds[0])?.name || "Choose pipeline"
      : `${selectedKanbanIds.length} pipelines selected`;

  const selectedKanbanButton = (
    <Container 
      $hasValue={selectedKanbanIds.length > 0}
    >
      {selectedKanbanName}
    </Container>
  );

  return (
    <CustomModal
      headingText="Add to pipeline"
      descriptionText="Choose a pipeline to add contacts."
      closeButton
      bodyPadding={"0 24px 0px 24px;"}
      onCancelHandler={() => {
        hideModal();
        if (onClose) onClose();
      }}
    >
      <Column gap="24px">
        <Column gap="8px">
          <StyledDropdownMenu
            selectStyle
            tooltipMessage={selectedKanbanName}
            title={selectedKanbanButton}
            show={isDropdownOpen}
            onToggle={setIsDropdownOpen}
            disabled={loading}
          >
            {loading ? (
              <EmptyKanbansContainer>
                <Text $description $faded>
                  Loading pipelines...
                </Text>
              </EmptyKanbansContainer>
            ) : kanbans && kanbans.length > 0 ? (
              kanbans.map((kanban) => {
                const dropdownItemContent = (
                  <Row alignItems spaceBetween>
                    <Row alignItems gap="8px">
                      <Checkbox
                        checked={selectedKanbanIds.includes(kanban._id)}
                        checkboxId={kanban._id}
                        onClickHandler={(e) => {
                          e.stopPropagation();
                          handleKanbanSelect(kanban._id);
                        }}
                        $height="16px"
                      />
                      <Text>{kanban.name}</Text>
                    </Row>
                  </Row>
                );

                return (
                  <div key={kanban._id}>
                    <DropdownItem
                      onClickHandler={(e) => {
                        e.stopPropagation();
                        handleKanbanSelect(kanban._id);
                      }}
                      $disabled={false}
                    >
                      {dropdownItemContent}
                    </DropdownItem>
                  </div>
                );
              })
            ) : (
              <EmptyKanbansContainer>
                <Text $description $faded>
                  No pipelines created yet
                </Text>
              </EmptyKanbansContainer>
            )}
          </StyledDropdownMenu>
        </Column>

        <Row spaceBetween style={{ flexDirection: "row-reverse" }}>
          <Button
            onClickHandler={handleSubmit}
            disabled={selectedKanbanIds.length === 0 || submitting}
            $type="blue"
            isLoading={submitting}
            $width="auto"
            $customDisabled={styles.colors.primary200}
          >
            {submitting ? "Adding..." : "Add to pipeline"}
          </Button>
        </Row>
      </Column>
    </CustomModal>
  );
};

const Container = styled.div<{ $hasValue?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 34px;
  border: none;
  width: 100%;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  line-height: 20px;
  font-family: NunitoSans;
  font-weight: 400;
  leading-trim: NONE;
  letter-spacing: 0px;
  color:  ${({ $hasValue }) => $hasValue ? styles.colors.black600 : styles.colors.black300}
`;

const StyledDropdownMenu = styled(DropdownMenu)`
  border: none;
  .actions-dropdown + div {
    width: 100%;
    max-height: 350px;
    overflow: hidden !important;
  }
`;

const EmptyKanbansContainer = styled.div`
  padding: 16px 12px;
  text-align: center;
  color: ${styles.colors.black400};
`;

export default AddToKanbanModal;

